import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Loader2, FileText, AlertTriangle, Search } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { listSiteReportsReview, getReportDetail } from "@/lib/review.functions";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_authenticated/review/reports")({
  component: ReportsReviewPage,
});

type Selected = { kind: "shift" | "incident"; id: string } | null;

function ReportsReviewPage() {
  const { currentOrg } = useAuth();
  const orgId = currentOrg?.id;
  const [kind, setKind] = useState<"all" | "shift" | "incident">("all");
  const [from, setFrom] = useState<string>("");
  const [to, setTo] = useState<string>("");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<Selected>(null);

  const fetchReports = useServerFn(listSiteReportsReview);
  const { data, isLoading } = useQuery({
    queryKey: ["review-reports", orgId, kind, from, to],
    queryFn: () =>
      fetchReports({
        data: {
          orgId: orgId!,
          kind,
          from: from || undefined,
          to: to || undefined,
          limit: 150,
        },
      }),
    enabled: !!orgId,
  });

  const reports = data?.reports ?? [];
  const filtered = reports.filter((r) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      r.title.toLowerCase().includes(q) ||
      r.siteName.toLowerCase().includes(q) ||
      r.guardName.toLowerCase().includes(q) ||
      r.type.toLowerCase().includes(q)
    );
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-2 rounded-xl border border-white/10 bg-white/[0.03] p-3">
        <Field label="Type">
          <select
            value={kind}
            onChange={(e) => setKind(e.target.value as "all" | "shift" | "incident")}
            className="rounded-md border border-white/10 bg-black/40 px-2 py-1.5 text-sm text-white"
          >
            <option value="all">All</option>
            <option value="shift">Shift Summary</option>
            <option value="incident">Incident</option>
          </select>
        </Field>
        <Field label="From">
          <input
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            className="rounded-md border border-white/10 bg-black/40 px-2 py-1.5 text-sm text-white"
          />
        </Field>
        <Field label="To">
          <input
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            className="rounded-md border border-white/10 bg-black/40 px-2 py-1.5 text-sm text-white"
          />
        </Field>
        <Field label="Search">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[#999]" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search reports..."
              className="rounded-md border border-white/10 bg-black/40 pl-8 pr-3 py-1.5 text-sm text-white placeholder:text-[#666]"
            />
          </div>
        </Field>
      </div>

      {isLoading ? (
        <div className="flex items-center gap-2 py-12 text-[#C0C0C0]">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading reports…
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-xl border border-white/10 bg-white/[0.03] px-6 py-12 text-center text-sm text-[#999999]">
          No reports in the selected filters.
        </div>
      ) : (
        <ul className="divide-y divide-white/5 overflow-hidden rounded-xl border border-white/10 bg-white/[0.03]">
          {filtered.map((r) => (
            <li key={`${r.kind}-${r.id}`}>
              <button
                type="button"
                onClick={() => setSelected({ kind: r.kind, id: r.id })}
                className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-white/[0.05]"
              >
                <div
                  className={cn(
                    "flex h-9 w-9 shrink-0 items-center justify-center rounded-lg",
                    r.kind === "incident"
                      ? "bg-[#CC0000]/15 text-[#FF8A8A]"
                      : "bg-white/10 text-[#C0C0C0]",
                  )}
                >
                  {r.kind === "incident" ? (
                    <AlertTriangle className="h-4 w-4" />
                  ) : (
                    <FileText className="h-4 w-4" />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-semibold text-white">{r.title}</div>
                  <div className="mt-0.5 truncate text-xs text-[#999999]">
                    {r.type} · {r.siteName} · {r.guardName}
                  </div>
                </div>
                <div className="text-xs text-[#C0C0C0]">
                  {new Date(r.date).toLocaleString()}
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}

      <ReportDetailDialog
        selected={selected}
        orgId={orgId}
        onClose={() => setSelected(null)}
      />
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1 text-[11px] uppercase tracking-wide text-[#C0C0C0]">
      <span>{label}</span>
      {children}
    </label>
  );
}

function ReportDetailDialog({
  selected,
  orgId,
  onClose,
}: {
  selected: Selected;
  orgId?: string;
  onClose: () => void;
}) {
  const fetchDetail = useServerFn(getReportDetail);
  const { data, isLoading } = useQuery({
    queryKey: ["report-detail", selected?.kind, selected?.id],
    queryFn: () =>
      fetchDetail({
        data: { orgId: orgId!, kind: selected!.kind, id: selected!.id },
      }),
    enabled: !!(selected && orgId),
  });

  return (
    <Dialog open={!!selected} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{selected?.kind === "incident" ? "Incident Report" : "Shift Summary"}</DialogTitle>
        </DialogHeader>
        {isLoading ? (
          <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : (
          <pre className="max-h-[60vh] overflow-auto rounded-md bg-black/40 p-3 text-[11px] leading-snug text-[#C0C0C0]">
            {JSON.stringify(data?.record ?? {}, null, 2)}
          </pre>
        )}
      </DialogContent>
    </Dialog>
  );
}
